import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { EditorState } from 'lexical'
import React, { useEffect } from 'react'

import { StyledEditor } from './Editor.styles'
import { themeConfig as ThemeConfig } from './Editor.theme'
import { EditorAutoLinkPlugin } from './plugins/AutoLinkPlugin'
import { GroupMentionsPlugin } from './plugins/GroupMentionsPlugin'
import { MentionsPlugin } from './plugins/MentionsPlugin'
import { Toolbar } from './Toolbar'
import { editorNodes } from './util/editorNodes'

const editorConfig = {
  // The editor theme
  theme: ThemeConfig,
  // Handling of errors during update
  onError(error) {
    throw error
  },
  namespace: 'mc-editor',
  // Any custom nodes go here
  nodes: editorNodes,
}

type Props = {
  activeCommentToReply: number | undefined
  setComment: (editorState: EditorState) => void
  successCount?: number
}

export function Editor({ setComment, successCount, activeCommentToReply }: Props) {
  function handleOnChange(editorState: EditorState) {
    setComment(editorState)
  }

  return (
    <StyledEditor isReplyMode={!!activeCommentToReply}>
      <LexicalComposer initialConfig={editorConfig}>
        <div className="editor-container">
          <Toolbar />
          <div className="editor-inner">
            <RichTextPlugin
              ErrorBoundary={LexicalErrorBoundary}
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={<Placeholder activeCommentToReply={activeCommentToReply} />}
            />
            <HistoryPlugin />
            <CustomAutoFocusPlugin activeCommentToReply={activeCommentToReply} />
            <ListPlugin />
            <LinkPlugin />
            <EditorAutoLinkPlugin />
            <OnChangePlugin onChange={handleOnChange} />
            <MentionsPlugin />
            <GroupMentionsPlugin />
            <ResetPlugin successCount={successCount} />
          </div>
        </div>
      </LexicalComposer>
    </StyledEditor>
  )
}

function Placeholder({ activeCommentToReply }: { activeCommentToReply: number | undefined }) {
  return <div className="editor-placeholder">{activeCommentToReply ? 'Reply to Comment' : 'Add comment'}</div>
}

function CustomAutoFocusPlugin({ activeCommentToReply }: { activeCommentToReply: number | undefined }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    editor.focus()
  }, [editor])

  useEffect(() => {
    activeCommentToReply && editor.focus()
  }, [activeCommentToReply, editor])

  return null
}

function ResetPlugin({ successCount }: { successCount?: number }) {
  const [editor] = useLexicalComposerContext()
  const emptyValue =
    '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}'

  useEffect(() => {
    if (successCount && successCount > 0) {
      try {
        const state = editor.parseEditorState(emptyValue)
        editor.setEditorState(state)
      } catch {
        // eslint-disable-next-line no-console
        console.warn('error reseting editor')
      }
      editor.blur()
    }
  }, [editor, successCount])
  if (!successCount) return null

  return null
}
